// ── @matix/api-client/client ──────────────────────────────────────────────
//
// Shared axios instance factory. Mirrors warsha's services/api.js patterns:
//   - JWT in Authorization header (Bearer)
//   - Global activeRequests counter for top-loading-bar UX
//   - 401 handler clears token and redirects to /login
//
// Per-app differences are passed in via createApiClient(config):
//   - storage namespace ('pos:cashier' / 'pos:kds' / 'pos:admin')
//   - onUnauthorized() callback (so each app handles redirect its own way)
//   - onLoaderChange(active) callback (so each app updates its own loader UI)
//
// In dev, baseURL is empty (/api) and Vite proxies to localhost:3004.
// In prod, baseURL is read from VITE_API_BASE_URL.

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { createStorage, type Storage } from '@matix/utils';
import type { ApiError } from '@matix/types';

export interface ApiClientConfig {
  /** localStorage namespace for token isolation between apps */
  storageNamespace: string;
  /** Override base URL; defaults to import.meta.env.VITE_API_BASE_URL || '' */
  baseURL?: string;
  /** Called when any 401 is received. Token is already cleared at call time. */
  onUnauthorized?: () => void;
  /** Called whenever the active-request count changes; `n` is the new count. */
  onLoaderChange?: (activeRequests: number) => void;
  /** Default request timeout in ms (default 30s). */
  timeout?: number;
  /** Optional static API key (for service-mode usage; usually unused on client). */
  apiKey?: string;
}

export interface ApiClient {
  axios: AxiosInstance;
  storage: Storage;
  setToken: (token: string | null) => void;
  getToken: () => string | null;
  clearToken: () => void;
}

const TOKEN_KEY = 'token';

export function createApiClient(config: ApiClientConfig): ApiClient {
  const storage = createStorage(config.storageNamespace);
  // Read VITE_API_BASE_URL. This MUST be written as the single literal member
  // expression `import.meta.env.VITE_API_BASE_URL` so Vite statically inlines
  // the value at build time. Aliasing (`const m = import.meta; m.env[...]`) or
  // dynamic-key access defeats the replacement and the value is silently
  // dropped from the bundle (baseURL then falls back to '' in production).
  // The cast keeps this compiling without a vite/client types dependency.
  const envBase = (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } })
    .env?.VITE_API_BASE_URL;
  const baseURL = config.baseURL ?? (envBase ? String(envBase) : '');

  const instance = axios.create({
    baseURL,
    timeout: config.timeout ?? 30_000,
    headers: { 'Content-Type': 'application/json' },
  });

  // ── activeRequests counter ───────────────────────────────────────────────
  let activeRequests = 0;
  const bumpLoader = (delta: number) => {
    activeRequests = Math.max(0, activeRequests + delta);
    config.onLoaderChange?.(activeRequests);
  };

  // ── Request interceptor: inject Bearer + optional API key ────────────────
  instance.interceptors.request.use((req: InternalAxiosRequestConfig) => {
    bumpLoader(+1);
    const token = storage.get<string>(TOKEN_KEY);
    if (token && !req.headers.has('Authorization')) {
      req.headers.set('Authorization', `Bearer ${token}`);
    }
    if (config.apiKey && !req.headers.has('x-api-key')) {
      req.headers.set('x-api-key', config.apiKey);
    }
    return req;
  });

  // ── Response interceptor: 401 handling + loader cleanup ──────────────────
  instance.interceptors.response.use(
    (res) => {
      bumpLoader(-1);
      return res;
    },
    (err: AxiosError<ApiError>) => {
      bumpLoader(-1);
      if (err.response?.status === 401) {
        storage.remove(TOKEN_KEY);
        config.onUnauthorized?.();
      }
      return Promise.reject(err);
    },
  );

  return {
    axios: instance,
    storage,
    setToken: (token) => {
      if (token) storage.set(TOKEN_KEY, token);
      else storage.remove(TOKEN_KEY);
    },
    getToken: () => storage.get<string>(TOKEN_KEY),
    clearToken: () => storage.remove(TOKEN_KEY),
  };
}

// ── Helper: extract error message from an AxiosError ─────────────────────
/** The server's machine-readable `code` on a 4xx/5xx envelope, if any. */
export function getApiErrorCode(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { code?: unknown } | undefined;
    return typeof data?.code === 'string' ? data.code : null;
  }
  return null;
}

/** The `details` object the server attached to an error envelope, if any. */
export function getApiErrorDetails<T = unknown>(err: unknown): T | null {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { details?: T } | undefined;
    return data?.details ?? null;
  }
  return null;
}

export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiError | undefined;
    return data?.error ?? err.message ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export type { AxiosInstance, AxiosRequestConfig };
